/**
 * The client's check for in-app navigation targets taken from URLs (deep
 * links, `?redirect=`, completion return paths). Mirrors the API helper: the
 * path must survive the browser's own normalization as a plain, same-origin,
 * single-slash app path, so `//evil.example`, `/..//evil.example`,
 * `/\evil.example` and encoded variants cannot navigate off-origin.
 *
 * Returns the normalized `pathname + search + hash`, or null.
 */
const BASE = 'https://app.invalid'
const UNSAFE_ENCODED = /%(2f|5c|2e)/i
// eslint-disable-next-line no-control-regex
const CONTROL_CHARACTERS = /[\u0000-\u001f\u007f]/

const hasDotSegment = (path: string) =>
  path.split('/').some(segment => segment === '..' || segment === '.')

export function toSafeAppPath(path: unknown): string | null {
  if (typeof path !== 'string') return null
  const raw = path.trim()
  if (
    !raw.startsWith('/') ||
    raw.startsWith('//') ||
    raw.includes('\\') ||
    CONTROL_CHARACTERS.test(raw) ||
    // Encoded separators only matter in the path; a query may carry them.
    UNSAFE_ENCODED.test(raw.split(/[?#]/)[0] ?? '') ||
    hasDotSegment(raw.split(/[?#]/)[0] ?? '')
  ) {
    return null
  }
  let parsed: URL
  try {
    parsed = new URL(raw, BASE)
  } catch {
    return null
  }
  if (parsed.origin !== BASE) return null
  const { pathname } = parsed
  if (
    !pathname.startsWith('/') ||
    pathname.startsWith('//') ||
    pathname.includes('\\') ||
    hasDotSegment(pathname)
  ) {
    return null
  }
  return `${pathname}${parsed.search}${parsed.hash}`
}
